/**
 * The confirmation horizon — SPEC §3.3.
 *
 * ## The horizon belongs to the person, not the month (§3.3.1)
 *
 * "I can only see three weeks ahead" is a fact about an **instructor**. The first version
 * stored it per month, which permitted a state that cannot be true: unsure past September 15
 * while simultaneously confirming all of October.
 *
 * So a horizon spans forward. Set "sure through September 15" and everything after it is
 * unconfirmed — the rest of September, and every look-ahead month shown beside it.
 *
 * ## But submitting a month overrides it (§3.3.1)
 *
 * Submitting a month whose horizon sits entirely *before* it is a positive statement: "I do
 * know October." That override exists because one control expresses two situations —
 * a plain visibility limit ("I plan two weeks out") and bounded uncertainty ("a wedding
 * lands somewhere in late September; October is normal"). Defaulting to the first and
 * letting the second override serves both without asking anyone to explain themselves.
 *
 * ## Nothing here is stored
 *
 * One nullable date on `Instructor` is the entire state. Whether a given day is provisional,
 * whether a month is confirmed in full, and how the counts split are all computed from it.
 * A stored `confirmedInFull` flag would be a second copy of a derivable fact, and the two
 * would drift the moment someone edited one and not the other (STATUS decision 1).
 */
import { addDays, compare, parseISO, } from '../dates/calendar.js';
import { firstDayOf, lastDayOf } from '../cycle/cycle.js';
/**
 * A horizon whose date has passed clears itself silently — SPEC §3.3.4.
 *
 * "Through September 15" is meaningless in October. It is stale data rather than a decision
 * to put to anybody, so it is dropped on read instead of being turned into a question.
 * Deriving this rather than running a nightly job means there is no window in which a stale
 * horizon is still being enforced.
 */
export function effectiveHorizon(horizon, today) {
    if (!horizon)
        return null;
    return compare(parseISO(horizon), today) < 0 ? null : horizon;
}
/** Whether the horizon date lands within this month. */
export function horizonFallsInside(horizon, cycleKey) {
    if (!horizon)
        return false;
    const date = parseISO(horizon);
    return compare(date, firstDayOf(cycleKey)) >= 0 && compare(date, lastDayOf(cycleKey)) <= 0;
}
/**
 * Whether the horizon shadows this month at all — the §3.3.1 override in one place.
 *
 * False when there is no horizon, and false when the instructor submitted this month while
 * the horizon sat entirely before it.
 */
export function horizonApplies({ cycleKey, horizon, submitted }) {
    if (!horizon)
        return false;
    if (submitted && !horizonFallsInside(horizon, cycleKey))
        return false;
    return true;
}
/** The inverse, for reading at call sites where the positive statement is what matters. */
export function isConfirmedInFull(ctx) {
    return !horizonApplies(ctx) || compare(parseISO(ctx.horizon), lastDayOf(ctx.cycleKey)) >= 0;
}
/**
 * Whether a marked day is provisional rather than confirmed — SPEC §3.3.5.
 *
 * Provisional is **not** unavailable (§3.3.2). The mark stands; it is simply excluded from
 * coverage until somebody confirms it.
 */
export function isProvisional(date, ctx) {
    if (!horizonApplies(ctx))
        return false;
    return compare(parseISO(date), parseISO(ctx.horizon)) > 0;
}
/** True when the horizon shadows every day of the month — the look-ahead case. */
export function shadowsWholeMonth(ctx) {
    if (!horizonApplies(ctx))
        return false;
    return compare(parseISO(ctx.horizon), firstDayOf(ctx.cycleKey)) < 0;
}
/**
 * The first day of the month the instructor has not confirmed, or null when none.
 *
 * Drives both the follow-up window (§6) and the "we'll ask you about the rest" copy.
 */
export function firstUnconfirmedDay(ctx) {
    if (!horizonApplies(ctx))
        return null;
    const first = firstDayOf(ctx.cycleKey);
    const last = lastDayOf(ctx.cycleKey);
    const through = parseISO(ctx.horizon);
    if (compare(through, first) < 0)
        return first;
    if (compare(through, last) >= 0)
        return null;
    // addDays goes through epoch days, so month-end and year-end need no special case.
    return addDays(through, 1);
}
/**
 * Split anything date-bearing into confirmed and unconfirmed — SPEC §3.3.3.
 *
 * Coverage counts `confirmed` only: a provisional day is not cover, and building a class on
 * one would be building on a day nobody committed to. But **both** numbers are shown wherever
 * they matter, because "5 confirmed, 4 unconfirmed" says a phone call might close the gap,
 * which neither "9 days" nor "5 days" says on its own.
 */
export function splitByHorizon(items, ctx) {
    const confirmed = [];
    const unconfirmed = [];
    for (const item of items) {
        (isProvisional(item.date, ctx) ? unconfirmed : confirmed).push(item);
    }
    return { confirmed, unconfirmed };
}
export function countByHorizon(items, ctx) {
    const { confirmed, unconfirmed } = splitByHorizon(items, ctx);
    return { confirmed: confirmed.length, unconfirmed: unconfirmed.length, total: items.length };
}
/**
 * Counts as a single admin-facing phrase — `2 confirmed · 1 unconfirmed`.
 *
 * Falls back to a plain day count when nothing is provisional, so an ordinary month does not
 * carry vocabulary it does not need.
 */
export function describeCounts(counts) {
    if (counts.unconfirmed === 0) {
        return `${counts.confirmed} day${counts.confirmed === 1 ? '' : 's'}`;
    }
    return `${counts.confirmed} confirmed \u00b7 ${counts.unconfirmed} unconfirmed`;
}
//# sourceMappingURL=horizon.js.map